import axiosInstance from '../../../config/axiosInstance';

export type Mode = 'purchase_risk' | 'margin_integrity';

export type StandardCalibrationInputs = {
  /**
   * Estándar declarado de profit (€/kg) contra el que calibrar.
   * Opcional; si no se envía, no se calcula calibración vs estándar.
   */
  standard_reference_profit_per_kg?: number;

  /**
   * Umbral significativo (€/kg, >= 0).
   * Solo aplica si standard_reference_profit_per_kg existe.
   */
  standard_significant_deviation_profit_per_kg?: number;
};

export type CommonV2Inputs = StandardCalibrationInputs & {
  supplier: string;
  n_sims?: number;
  random_seed?: number;
  target_quantity_in_kg?: number;
  sell_price_overrides_by_format?: Record<string, number>;
};

export type PurchaseRiskRequest = CommonV2Inputs & {
  mode: 'purchase_risk';
  risk_target: number;
  price_min: number;
  price_max?: number;
};

export type MarginIntegrityRequest = CommonV2Inputs & {
  mode: 'margin_integrity';
  purchase_price_eur_per_kg: number;
  downside_quantile?: number;
};

export type SimulateV2Request = PurchaseRiskRequest | MarginIntegrityRequest;

/**
 * Nota: respuesta tipada como unknown para no inventar estructura completa..
 * La UI extraerá solo campos conocidos (profit_per_kg_input, standard_calibration, etc.) con accesos seguros.
 */
export async function simulateHistoricalV2(body: SimulateV2Request): Promise<unknown> {
  const { data } = await axiosInstance.post('/industrial-risk/simulate-historical-v2', body);
  return data as unknown;
}

export async function getIndustrialRiskOverview(params?: {
  risk_target?: number;
  n_sims?: number;
  max_suppliers?: number;
  baseline?: 'weighted_avg' | 'last';
  price_min_factor?: number;
  standard_reference_profit_per_kg?: number;
  standard_significant_deviation_profit_per_kg?: number;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/overview', { params });
  return data as unknown;
}

export async function getIndustrialRiskCostsSummary(params?: {
  period_key?: string;
  top_n_suppliers?: number;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/costs/summary', { params });
  return data as unknown;
}

export async function getIndustrialRiskYieldSummary(params?: {
  period_key?: string;
  max_suppliers?: number;
  max_rows?: number;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/yield-summary', { params });
  return data as unknown;
}

export async function getIndustrialRiskQualityMix(params?: {
  period_key?: string;
  product_code?: string;
  limit_lots?: number;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/quality-mix', { params });
  return data as unknown;
}

export async function getIndustrialRiskQualityMixBySupplier(params?: {
  period_key?: string;
  product_code?: string;
  top_n?: number;
  sort_by?: 'premium_share' | 'low_share' | 'units' | 'revenue';
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/quality-mix-by-supplier', { params });
  return data as unknown;
}

export async function getIndustrialRiskQualityMixSupplier(params: {
  supplier: string;
  period_key?: string;
  product_code?: string;
  top_skus?: number;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/quality-mix-supplier', { params });
  return data as unknown;
}

export async function getIndustrialRiskQualityIndex(params: {
  supplier: string;
  period_key?: string;
  product_code?: string;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/quality-index', { params });
  return data as unknown;
}

export async function getIndustrialRiskGlobalDistribution(params?: {
  n_sims?: number;
  bins?: number;
  max_suppliers?: number;
  baseline?: 'weighted_avg' | 'last';
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/global-distribution', { params });
  return data as unknown;
}

export async function getIndustrialRiskRiskDrivers(params?: {
  scope?: 'global' | 'supplier';
  supplier?: string;
  baseline?: 'weighted_avg' | 'last';
  n_sims?: number;
  alpha?: number;
  random_seed?: number | null;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/risk-drivers', { params });
  return data as unknown;
}

export async function getIndustrialRiskFragility(params?: {
  scope?: 'global' | 'supplier';
  supplier?: string;
  baseline?: 'weighted_avg' | 'last';
  n_sims?: number;
  alpha?: number;
  random_seed?: number | null;

  shock_price_delta?: number;
  shock_yield_delta_abs?: number;

  h_price?: number;
  h_yield_abs?: number;

  loss_target?: number;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/fragility', { params });
  return data as unknown;
}

export async function getIndustrialRiskSuppliers(): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/suppliers');
  return data as unknown;
}

export async function getIndustrialRiskPurchaseEnvelope(params: {
  supplier: string;
  baseline?: 'weighted_avg' | 'last';
  risk_targets?: number[];
  n_sims?: number;
  random_seed?: number | null;
}): Promise<unknown> {
  const { data } = await axiosInstance.get('/industrial-risk/data/purchase-envelope', { params });
  return data as unknown;
}